/**
 * Internal Airtable HTTP helper — private to the service layer.
 * Components must NEVER import from this file directly.
 * All public data access goes through a domain service file in this directory.
 */

/* ── Throttled request queue + 429 retry ─────────────────────────────────
 * Airtable allows ~5 requests/second per base and returns 429 (and then
 * blocks for ~30s) when exceeded. The service layer previously fired every
 * request immediately with no retry, which flooded the base (e.g. paginating
 * the 1500+ Contacts table alongside everything else) and stalled loads.
 *
 * All service calls now funnel through a small concurrency-capped queue with
 * exponential-backoff retry on 429. Retrying is safe even for POST/PATCH/
 * DELETE: a 429 means Airtable rejected the request without processing it.
 */
const _MAX_CONCURRENT = 3
// Concurrency alone doesn't keep us under Airtable's ~5 req/sec cap — 3 fast responses can still
// fire well over 5/sec back-to-back. Space out request STARTS too (a screen like the Billing Run,
// which prefetches phases/invoices/contacts/etc. all at once, was tripping 429s on load because of
// this gap — the queue's own retry masked it from users, but it was still visible/noisy in devtools).
// 210ms (~4.76/sec) was still occasionally tripping one 429 on a cold Billing Run load — right at
// Airtable's edge, where ordinary event-loop/setTimeout jitter is enough to tip a rolling window
// over 5. 260ms (~3.8/sec) gives real margin.
const _MIN_START_GAP_MS = 260
let _active = 0
let _lastStart = 0
let _pumpTimer = null
const _queue = []
// Background loads (opts.low) wait behind everything else. The home list's expanded project rows
// queue two reads per project (44 projects after an "Expand all" = ~25s of requests); in one
// first-come line, a project opened during that window waited for all of them before its own
// concepts / sheets loaded. Low-priority jobs only start when no normal job is waiting.
const _lowQueue = []

function _pump() {
  if (_pumpTimer) return
  if (_active >= _MAX_CONCURRENT || (_queue.length === 0 && _lowQueue.length === 0)) return
  const wait = Math.max(0, _MIN_START_GAP_MS - (Date.now() - _lastStart))
  if (wait > 0) {
    _pumpTimer = setTimeout(() => { _pumpTimer = null; _pump() }, wait)
    return
  }
  const job = _queue.length ? _queue.shift() : _lowQueue.shift()
  _active++
  _lastStart = Date.now()
  _attempt(job.body, 0)
    .then(job.resolve, job.reject)
    .finally(() => { _active--; _pump() })
  _pump()
}

/* ── Auth headers ────────────────────────────────────────────────────────
 * /api/airtable requires a credential: a Clerk bearer (any signed-in user),
 * or — for the public client presentation view only — a journey token
 * (read-only, presentation tables only; see api/airtable.js).
 *
 * The Clerk getter (window.__clerkGetToken) is installed by OutlineView's
 * effect (and by each standalone page: Sketch, Modeler, Admin, Journey), but
 * an early caller can race it — wait briefly, mirroring services/notifications.js.
 * Public client pages never install the getter, so when a journey token is
 * present we skip the wait entirely.
 */
async function _authHeaders() {
  const headers = { 'Content-Type': 'application/json' }
  const journeyToken = typeof window !== 'undefined' ? window.__ajJourneyToken : null
  if (journeyToken) headers['x-aj-journey-token'] = journeyToken
  const tries = journeyToken ? 1 : 20
  for (let i = 0; i < tries; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try {
        const t = await window.__clerkGetToken()
        if (t) headers['Authorization'] = 'Bearer ' + t
      } catch (e) { /* signed out or Clerk not ready — send without */ }
      break
    }
    if (i < tries - 1) await _delay(150)
  }
  return headers
}

async function _attempt(body, retries) {
  let res
  try {
    res = await fetch('/api/airtable', {
      method: 'POST',
      headers: await _authHeaders(),
      body: JSON.stringify(body),
    })
  } catch (e) {
    if (retries < 3) { await _delay(500 * (retries + 1)); return _attempt(body, retries + 1) }
    throw e
  }
  if (res.status === 429 && retries < 6) {
    await _delay(1000 * (retries + 1))
    return _attempt(body, retries + 1)
  }
  const text = await res.text()
  try { return JSON.parse(text) } catch (e) { return {} }
}

function _delay(ms) { return new Promise(r => setTimeout(r, ms)) }

function airtable(body, low) {
  return new Promise((resolve, reject) => {
    ;(low ? _lowQueue : _queue).push({ body, resolve, reject })
    _pump()
  })
}

/** GET — returns the records array (empty array on failure). opts.low = background load, served last. */
export function apiGet(tbl, qs, opts) {
  return airtable({ method: 'GET', tbl, qs }, !!(opts && opts.low)).then(d => d.records || [])
}

/** GET a single record by ID — returns the record object ({ id, fields }) or null. */
export function apiGetRecord(tbl, id) {
  return airtable({ method: 'GET', tbl: tbl + '/' + id, qs: '' }).then(d => (d && d.id ? d : null))
}

/**
 * GET one page — returns { records, nextOffset } so callers can paginate
 * manually without loading the full table. nextOffset is null when done.
 */
export function apiGetPage(tbl, qs) {
  return airtable({ method: 'GET', tbl, qs }).then(d => ({
    records:    d.records   || [],
    nextOffset: d.offset    || null,
  }))
}

/** PATCH a single record — returns the raw Airtable response. opts.typecast = lenient select matching. */
export function apiPatch(tbl, id, fields, opts) {
  return airtable({ method: 'PATCH', tbl, id, fields, ...(opts && opts.typecast ? { typecast: true } : {}) })
}

/** POST a single record — returns the raw Airtable response. opts.typecast = lenient select matching. */
export function apiPost(tbl, fields, opts) {
  return airtable({ method: 'POST', tbl, fields, ...(opts && opts.typecast ? { typecast: true } : {}) })
}

/**
 * POST records in batches of 10.
 * Throws if any batch returns an error.
 * Returns a flat array of all created records.
 */
export async function apiPostBatch(tbl, records) {
  const results = []
  for (let i = 0; i < records.length; i += 10) {
    const batch = records.slice(i, i + 10)
    const res = await airtable({ method: 'POST', tbl, records: batch })
    if (res.error) throw new Error(res.error)
    if (res.records) results.push(...res.records)
  }
  return results
}

/**
 * PATCH records in batches of 10. Each record is { id, fields }.
 * If a whole batch fails, retries that batch one record at a time so we can
 * report exactly which records failed (used by the Gantt write-back).
 * @returns {Promise<{ succeeded: string[], failed: {id: string, error: string}[] }>}
 */
export async function apiPatchBatch(tbl, records) {
  const succeeded = []
  const failed = []
  for (let i = 0; i < records.length; i += 10) {
    const batch = records.slice(i, i + 10)
    const res = await airtable({ method: 'PATCH', tbl, records: batch })
    if (!res.error && res.records) {
      succeeded.push(...res.records.map(r => r.id))
      continue
    }
    // Batch failed — fall back to per-record so partial success is preserved.
    for (const rec of batch) {
      const one = await airtable({ method: 'PATCH', tbl, id: rec.id, fields: rec.fields })
      if (one.error || !one.id) failed.push({ id: rec.id, error: (one.error && (one.error.message || one.error)) || 'PATCH failed' })
      else succeeded.push(one.id)
    }
  }
  return { succeeded, failed }
}

/** GET a single Airtable record by ID — returns { id, fields } or null */
export function apiGetOne(tbl, id) {
  return airtable({ method: 'GET', tbl: tbl + '/' + id, qs: '' }).then(d => (d && d.id) ? d : null)
}

/**
 * GET a single record, telling a DELETED record apart from a failed request: resolves the record,
 * `null` only when Airtable answers NOT_FOUND, and throws on anything else (rate limit, proxy
 * failure). Use it where "gone" triggers an action, so a hiccup is never read as a delete.
 */
export async function apiGetOneStrict(tbl, id) {
  const d = await airtable({ method: 'GET', tbl: tbl + '/' + id, qs: '' })
  if (d && d.id) return d
  const err = d && d.error
  const code = typeof err === 'string' ? err : (err && (err.type || err.message)) || ''
  if (/NOT_FOUND/i.test(String(code))) return null
  throw new Error(String(code || 'Airtable request failed'))
}

/** DELETE a single record — returns the raw Airtable response */
export function apiDelete(tbl, id) {
  return airtable({ method: 'DELETE', tbl, id })
}

/** DELETE records in batches of 10 (Airtable batch delete). */
export async function apiDeleteBatch(tbl, ids) {
  for (let i = 0; i < ids.length; i += 10) {
    await airtable({ method: 'DELETE', tbl, batchIds: ids.slice(i, i + 10) })
  }
}

/**
 * GET all records across all pages, following Airtable's offset pagination token.
 * Use this instead of apiGet for any table that may exceed 100 rows.
 * Returns a flat array of all records.
 */
export async function apiGetAll(tbl, qs, onPage, opts) {
  const allRecords = []
  let offset = null
  do {
    const pageQs = (qs || '') + (offset ? (qs ? '&' : '') + 'offset=' + encodeURIComponent(offset) : '')
    const data = await airtable({ method: 'GET', tbl, qs: pageQs })
    // A successful Airtable response always carries `records` (even []). Its absence = an error
    // (rate-limit block, proxy failure). With { strict:true } the caller wants that surfaced rather
    // than swallowed into a misleading empty list (e.g. the Billing Run showing 0 projects).
    if (opts && opts.strict && !Array.isArray(data.records)) {
      throw new Error((data && data.error && (data.error.message || data.error)) || 'Airtable request failed')
    }
    if (data.records) allRecords.push(...data.records)
    offset = data.offset || null
    // Progressive callback: large tables (e.g. Contacts) page sequentially;
    // onPage lets the caller render what's loaded so far after each page.
    if (onPage) { try { onPage(allRecords.slice(), !offset) } catch (e) {} }
  } while (offset)
  return allRecords
}
